/**
 * Waypoint and beeline-flag arithmetic, kept free of dependency injection so the
 * index handling can be verified directly.
 *
 * Beeline flags are *leg*-indexed: flag `i` describes the leg between waypoint `i`
 * and waypoint `i + 1`. Every structural change to the waypoint list therefore has to
 * shift the flags too, which is the subtlest part of the port from `Routing.js`.
 */

/** Guarantees exactly one flag per leg, padding with `false` and truncating extras. */
export const normaliseFlags = (flags: readonly boolean[], waypointCount: number): boolean[] => {
  const legCount = Math.max(0, waypointCount - 1);
  const next = new Array<boolean>(legCount).fill(false);
  for (let i = 0; i < Math.min(legCount, flags.length); i++) {
    next[i] = flags[i];
  }
  return next;
};

/**
 * Splits the leg at `index - 1` in two.
 *
 * The inserted waypoint lands at `index`, so both halves of the split leg inherit the
 * flag of the leg they replaced.
 */
export const flagsAfterInsert = (
  flags: readonly boolean[],
  index: number,
  waypointCountAfterInsert: number
): boolean[] => {
  const next = [...flags];
  const splitLeg = Math.max(0, index - 1);
  next.splice(splitLeg, 0, flags[splitLeg] ?? false);
  return normaliseFlags(next, waypointCountAfterInsert);
};

/** Drops the leg that the removed waypoint terminated. */
export const flagsAfterRemove = (
  flags: readonly boolean[],
  index: number,
  waypointCountAfterRemove: number
): boolean[] => {
  const next = [...flags];
  next.splice(Math.max(0, index - 1), 1);
  return normaliseFlags(next, waypointCountAfterRemove);
};

/** Legs keep their identity when the route is reversed, so the flags reverse with them. */
export const flagsAfterReverse = (flags: readonly boolean[]): boolean[] => [...flags].reverse();

/** Appending a waypoint adds one leg, optionally drawn straight. */
export const flagsAfterAppend = (
  flags: readonly boolean[],
  waypointCountAfterAppend: number,
  asBeeline: boolean
): boolean[] => {
  const next = normaliseFlags(flags, waypointCountAfterAppend);
  if (asBeeline && next.length > 0) {
    next[next.length - 1] = true;
  }
  return next;
};
